import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { projectApi, eachProjectFiles } from '../../../APIs/projectApi';
import { getAsync, useGet } from '../../../hooks/useApi';
import { ProjectModel } from '../../../models/ProjectModel';
import { AiTypes } from './AiTypes';
import { FolderIcon, SpinnerIcon } from '../../atomic/icons';
import { Map } from './Map';
import { LatLng } from 'leaflet';
import { Loading } from '../../atomic/effects';
import { ProjectUpdatesModelEntry } from '../../../models/ProjectUpdateModel';
import { fixPHPArrayFilter } from '../../../utils/fixPHPArrayFilter';
import { Tag } from '../../../utils/ServerClientModel';
import classNames from 'classnames';
import { ErrorContext } from '../../../contexts';
import { ErrorDisplay, NotFound } from '../../Errors';
import { FileModelMetadata } from '../../../models/FileModel';
import { Queue } from '../../../utils/queue';
import { isAbortError } from '../../../utils/abort';
import { userApi } from '../../../APIs/userApi';
import { getRoundedLocation, zoomToRound } from '../utils';
import { FilesAPI } from '../../../APIs/filesApi';
import { CheckBox } from '../../atomic/switches';
import { FileItemModel, FolderItemModel } from '../../../models/ItemModel';

export interface PointProject {
    projectId: string;
    types: string[];
    tags: Tag[];
    aiAnalysisTypeId: string;
    show?: boolean;
}

export interface PointFile {
    show: boolean;
    fileId: string;
    folderId: string;
    fileName: string;
    url: string;
    thumbUrl: string;
    metadata: FileModelMetadata;
    position: LatLng;
    projects: PointProject[];
}

export interface Point {
    key: string;
    position: LatLng;
    show: boolean;
    files: PointFile[];
}

export interface Points {
    [pointId: string]: Point;
}

const addFile = (
    points: Points,
    show: boolean,
    file: Omit<PointFile, 'position' | 'show'>,
    round: number
) => {
    const location = file.metadata.location!;
    const position = getRoundedLocation(
        location.latitude,
        location.longitude,
        round
    );
    const key: string = position.toString();
    const pointFile: PointFile = { ...file, show, position };

    if (points[key]) {
        // 同一ファイルはまとめる
        const sameFiles = points[key].files.filter(
            (file) => file.fileId === pointFile.fileId
        );
        if (sameFiles.length) {
            sameFiles.forEach((file, index) => {
                file.projects.push(pointFile.projects[0]);
            });
        } else {
            points[key].files.push(pointFile);
        }
        if (show) {
            points[key].show = show;
        }
    } else {
        const position = new LatLng(location.latitude, location.longitude);
        points[key] = {
            key,
            position,
            show,
            files: [pointFile],
        };
    }
};
const updatePositionsByLocation = (points: Points) => {
    for (let key in points) {
        let lat = 0;
        let lng = 0;
        let count = 0;
        points[key].files.forEach(({ metadata }) => {
            const location = metadata?.location;
            if (!location) {
                return;
            }
            lat += location.latitude;
            lng += location.longitude;
            count++;
        });
        if (count) {
            points[key].position = new LatLng(lat / count, lng / count);
        }
    }
};

type useMapFilterProps = {
    folderId?: string;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const useMapFilter = (opt: useMapFilterProps) => {
    const { folderId, setLoading } = opt;
    const defaultZoom = 16;
    const [points, setPoints] = React.useState<Points>({});
    const [center, setCenter] = React.useState<LatLng>(
        new LatLng(34.699, 135.5)
    );
    const [zoom, setZoom] = React.useState<number>(defaultZoom);
    const [filter, setFilter] = React.useState<Filter>({});
    const refFilter = React.useRef(filter);
    const refZoom = React.useRef(zoom);

    const [loadFiles, setLoadFiles] = React.useState<boolean>(false);
    const { setErrors } = React.useContext(ErrorContext);
    const [projects, setProjects] = React.useState<ProjectModel[] | null>(null);

    const updatePointsByFilter = (points: Points, filter: Filter) => {
        for (const key in points) {
            const point = points[key];
            //対象のファイルを特定
            point.files.map((file, fileIndex) => {
                // プロジェクトループ
                file.projects.forEach(({ types }, projectIndex) => {
                    file.projects[projectIndex].show = types.some(
                        (key) => filter[key]
                    );
                });
                // ファイルが表示対象かチェック
                point.files[fileIndex].show = point.files[
                    fileIndex
                ].projects.some(({ show }) => show);
            });

            // 表示対象のファイルがあるか？
            points[key].show = point.files.some(({ show }) => show);
        }
        return points;
    };
    useEffect(() => {
        refFilter.current = filter;
        setPoints((prevState) =>
            updatePointsByFilter(JSON.parse(JSON.stringify(prevState)), filter)
        );
    }, [filter]);

    const projectFileHandle = (
        project: ProjectModel,
        { item, update }: ProjectUpdatesModelEntry
    ) => {
        if (!update) return;
        const { file } = item;
        if (!file.metadata.location) {
            // 位置情報がない場合は処理を実施しない
            return;
        }
        const { compiled } = update;
        const tmpTagTypes = fixPHPArrayFilter(compiled.fileMeta.tags).map(
            (tag) => tag.type
        );
        const tagTypes = tmpTagTypes.filter(
            (tag, index) => tmpTagTypes.indexOf(tag) === index
        );
        if (!tagTypes.length) {
            return;
        }
        const filter = refFilter.current;
        const zoom = refZoom.current;
        const round = zoomToRound(zoom);
        const aiAnalysisTypeId =
            project.aiAnalysisTypeId ?? project.template.aiAnalysisTypeId;
        const types = tagTypes.map((type) =>
            getFilterKey(aiAnalysisTypeId, type)
        );
        setPoints((p) => {
            const _points = { ...p };
            addFile(
                _points,
                types.some((key) => filter[key]),
                {
                    fileId: file.fileId,
                    folderId: file.folderId,
                    fileName: file.fileName,
                    url: file.url,
                    thumbUrl: file.thumbUrl,
                    metadata: file.metadata,
                    projects: [
                        {
                            projectId: project.projectId,
                            types: types,
                            tags: fixPHPArrayFilter(compiled.fileMeta.tags),
                            aiAnalysisTypeId,
                        },
                    ],
                },
                round
            );
            updatePositionsByLocation(_points);
            return _points;
        });
    };

    useEffect(() => {
        if (projects) {
            setLoadFiles(true);
            const controller = new AbortController();
            const queue = new Queue({
                concurrency: 6,
                signal: controller.signal,
            });
            for (const project of projects) {
                queue.add(() =>
                    eachProjectFiles(
                        project.projectId,
                        { points: false, queue },
                        projectFileHandle
                    )
                );
            }
            queue
                .finish()
                .catch((err) => {
                    if (isAbortError(err)) return;
                    console.warn(err);
                    setErrors((errors) => [
                        ...errors,
                        err.message || 'ファイル情報の取得に失敗しました',
                    ]);
                })
                .finally(() => {
                    if (controller.signal.aborted) return;
                    setLoadFiles(false);
                });
            return () => {
                setLoadFiles(false);
                controller.abort();
            };
        }
    }, [projects]);

    useEffect(() => {
        folderProjects.mutate();
    }, []);

    useEffect(() => {
        refZoom.current = zoom;
        setPoints((points) => {
            const newPoints: Points = {};
            const round: number = zoomToRound(zoom);
            for (let key in points) {
                const point = points[key];
                point.files.forEach((file) => {
                    addFile(newPoints, file.show, file, round);
                });
            }
            updatePositionsByLocation(newPoints);
            for (let key in newPoints) {
                newPoints[key].show = newPoints[key].files.some(
                    ({ show }) => show
                );
            }
            return newPoints;
        });
    }, [zoom]);

    const { data: folder } = useGet(folderId ? FilesAPI.item(folderId) : null, {
        keepPreviousData: true,
    });

    // 子孫も含めたフォルダーのプロジェクトを取得
    const folderProjects = useGet(
        folderId ? projectApi.get.folderProjects(folderId, true) : null
    );
    const { data: getProjects, error, isLoading } = folderProjects;

    useEffect(() => {
        const _projects = getProjects?.data?.map((project: ProjectModel) => {
            return project;
        });
        if (_projects) {
            setProjects(_projects);
        }
        setLoading(false);
    }, [getProjects?.data]);

    const getFilterKey = (aiAnalysisTypeId: string, type: number) =>
        `${aiAnalysisTypeId}_${type}`;

    const onLabelTypeChange = React.useCallback(
        (aiAnalysisTypeId: string, type: number, checked: boolean) => {
            setFilter((prevState) => {
                const nextState = { ...prevState };
                nextState[getFilterKey(aiAnalysisTypeId, type)] = checked;
                return nextState;
            });
        },
        []
    );

    return {
        points,
        setPoints,
        center,
        setCenter,
        filter,
        setFilter,
        zoom,
        setZoom,
        refFilter,
        refZoom,
        projects,
        setProjects,
        folder,
        loadFiles,
        error,
        onLabelTypeChange,
    };
};

const useViewAllFileFilter = (folderId: string | undefined, zoom) => {
    const [allFilesPoints, setAllFilesPoints] = React.useState<Points>({});
    const [allFilesLoading, seAllFilesLoading] = React.useState<boolean>(true);

    const abortController = new AbortController();
    const fileQueue = new Queue({
        concurrency: 5,
        signal: abortController.signal,
    });

    React.useEffect(() => {
        if (folderId) {
            fileQueue.add((options) => getRootFolders(options, folderId, 0));
        }
    }, [folderId]);

    const addAllFiles = (item: FileItemModel) => {
        const round = zoomToRound(zoom);
        setAllFilesPoints((p) => {
            const _points = { ...p };
            const file = item.file;
            addFile(
                _points,
                true,
                {
                    fileId: file.fileId,
                    folderId: file.folderId,
                    fileName: file.fileName,
                    url: file.url,
                    thumbUrl: file.thumbUrl,
                    metadata: file.metadata,
                    projects: [],
                },
                round
            );

            updatePositionsByLocation(_points);

            return _points;
        });
    };

    //子フォルダも含めすべてのファイルを登録する
    const getRootFolders = async (
        config: { signal: AbortSignal },
        folderId: string,
        offset: number
    ) => {
        getAsync(
            FilesAPI._items({
                parentFolderId: folderId,
                idsOnly: false,
                visualFilesOnly: true,
                file: true,
                folder: true,
                frame: true,
                video: false,
                limit: 100,
                offset: offset,
            })
        ).then((resp) => {
            if (resp.data.length) {
                resp.data.map((item) => {
                    if (item.folderId) {
                        fileQueue.add((options) =>
                            getRootFolders(options, item.folderId, 0)
                        );
                    } else {
                        if (
                            item['file'] &&
                            item['file']['metadata']['location']
                        ) {
                            addAllFiles(item as FileItemModel);
                        }
                    }
                });
                if (resp.pagination.hasNextPage) {
                    fileQueue.add((options) =>
                        getRootFolders(
                            options,
                            folderId,
                            resp.pagination.nextOffset
                        )
                    );
                }
            }
            if (fileQueue.size === 0) {
                seAllFilesLoading(false);
            }
        });
    };

    return { allFilesLoading, seAllFilesLoading, allFilesPoints };
};

interface Filter {
    [key: string]: boolean;
}
export const MapProjects: React.FC = () => {
    const [showAllFiles, setShowAllFiles] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(true);

    const { folderId } = useParams();
    const {
        points,
        setPoints,
        center,
        //setCenter,
        //filter,
        setFilter,
        zoom,
        setZoom,
        //refFilter,
        //refZoom,
        projects,
        //setProjects,
        folder,
        loadFiles,
        error,
        onLabelTypeChange,
    } = useMapFilter({ folderId, setLoading });

    const { allFilesLoading, allFilesPoints } = useViewAllFileFilter(
        folderId,
        zoom
    );

    const { data: accessItemData } = useGet(
        folderId ? userApi.get.itemPermission(folderId) : null
    );
    React.useEffect(() => {
        if (!allFilesLoading) {
            console.log('ファイルデータ読み込み完了');
        }
        if (showAllFiles && allFilesLoading) {
            console.log('表示開始');
        }
    }, [allFilesLoading, showAllFiles]);

    if (accessItemData?.data.showAiResult == 0) {
        return <NotFound />;
    }
    if (loading || !folder?.data || !projects || !accessItemData) {
        return <Loading />;
    }

    if (error) {
        return <ErrorDisplay error={error} />;
    }

    return (
        <div className="app-map-main">
            <div className="context">
                <Map
                    points={points}
                    center={center}
                    onChangeZoom={setZoom}
                    defaultZoom={zoom}
                    showAllFiles={showAllFiles}
                    allFilePoint={allFilesPoints}
                />
            </div>
            <div className="sidebar">
                <div className={classNames('sidebar-header')}>
                    {(!loadFiles || !allFilesLoading) && <FolderIcon />}
                    {loadFiles && allFilesLoading && <SpinnerIcon />}
                    {folder.data && (
                        <Link to={`/app/folderlist/${folder.data.folderId}`}>
                            {folder.data.name}
                        </Link>
                    )}
                </div>
                <CheckBox
                    label="すべてのファイル"
                    name="all-files"
                    checked={showAllFiles}
                    _onChange={setShowAllFiles}
                />
                <AiTypes
                    hidden={showAllFiles}
                    onLabelTypeChange={onLabelTypeChange}
                    projects={projects}
                ></AiTypes>
            </div>
        </div>
    );
};
